import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bot, ShieldCheck, AlertTriangle, FileText, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const liveEvents = [
  { icon: Bot, text: "Агент проверил 1 500 постов за час", color: "text-primary" },
  { icon: AlertTriangle, text: "Найдено 12 ошибок SALE без перевода", color: "text-warning" },
  { icon: FileText, text: "Сформировано 5 апелляций в ФАС", color: "text-info" },
  { icon: ShieldCheck, text: "98% креативов прошли аудит ФЗ-168", color: "text-success" },
  { icon: Bot, text: "Выдано 340 erid-токенов за последний час", color: "text-primary" },
  { icon: AlertTriangle, text: "Обнаружено 3 нарушения пиксельной равнозначности", color: "text-warning" },
];

export function AiLiveActivitySkeleton() {
  return (
    <div className="w-full max-w-3xl" style={{ minHeight: 260 }}>
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-xl" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-3 w-3 rounded-full ml-auto" />
        </div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-7 w-7 rounded-lg shrink-0" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
        <Skeleton className="h-12 w-full rounded-2xl mt-4" />
      </div>
    </div>
  );
}

export function AiLiveActivity() {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (visibleCount < liveEvents.length) {
      const timer = setTimeout(() => setVisibleCount(v => v + 1), 600);
      return () => clearTimeout(timer);
    }
  }, [visibleCount]);

  return (
    <div className="w-full max-w-3xl" style={{ minHeight: 260 }}>
      <div className="glass-card p-4 sm:p-6 relative overflow-hidden">
        {/* Glow */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full opacity-20 blur-[60px]" style={{ background: "radial-gradient(circle, hsl(243 75% 59% / 0.5), transparent 70%)" }} />

        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-xl bg-primary/15">
            <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
          </div>
          <h3 className="text-xs sm:text-sm font-bold text-foreground uppercase tracking-wide">AI Live Activity</h3>
          <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5 ml-auto">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-success" />
          </span>
        </div>

        {/* Feed */}
        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          {liveEvents.map((event, i) => {
            const EventIcon = event.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -15 }}
                animate={i < visibleCount ? { opacity: 1, x: 0 } : { opacity: 0, x: -15 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="flex items-center gap-2 sm:gap-3"
              >
                <div className="flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-lg bg-secondary">
                  <EventIcon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${event.color}`} />
                </div>
                <span className="text-[11px] sm:text-sm text-foreground leading-tight">{event.text}</span>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <Button size="lg" asChild className="w-full gap-1.5 sm:gap-2 rounded-2xl py-4 sm:py-6 text-xs sm:text-base font-semibold shadow-lg shadow-primary/20">
          <Link to="/auth">
            <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="truncate">Присоединиться к защищённому контуру</span>
            <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
